/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { ChangeEvent, useState } from "react";
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from "react-redux";
import { useHistory } from 'react-router-dom';

import {
    Box, Dialog, DialogContent, DialogTitle, IconButton, FormControlLabel, TextField, Typography, Checkbox,
} from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import { createBrowserHistory } from 'history';

import Settings from "../../../conf/settings";
import {
    Application, ApplicationReducer, ApplicationState, UserReducer
} from "../../../data/interfaces";
import { createApplication, updateApplication } from "../../../data/operations";
import GreyButton from '../../../elements/GreyButton';
import { PreLoaderLogo } from "../../../elements/PreLoaderLogo";
import PrimaryButton from '../../../elements/PrimaryButton';
import CustomIcons from "../../../icons/CustomIcons";
import { useStyles } from "./style";
import {
    MAX_APPLICATION_DESCRIPTION_LENGTH, MAX_APPLICATION_NAME_LENGTH, PATH_SEPARATOR, RESOURCE_APPLICATIONS,
    RESOURCE_OVERVIEW
} from "../../../data/constants";
import {trackApplicationCreation} from "../../../util/app-insights";
import { isInvalidValidScopeName } from "../../../util/token-generation";
import CreateApplicationFormContent from "./CreateApplicationFormContent";

interface CreateAppProps {
    open: boolean,
    redirectToApplication: boolean,
    isEdit: boolean,
    application?: Application;
    applicationReducer: ApplicationReducer,
    userReducer: UserReducer,
    onClose: () => void,
    dispatchCreateApplication: (application: Application) => Promise<string|null>,
    dispatchUpdateApplication: (application: Application) => void,
}

function CreateApp(props: CreateAppProps) {
    const {
        open, redirectToApplication, onClose, isEdit, application, applicationReducer} = props;
    const classes = useStyles();
    const intl = useIntl();
    const isInProgress = applicationReducer?.isLoading;

    const [applicationName, setApplicationName] = useState(isEdit ? application?.name : '');
    const [description, setDescription] = useState(isEdit ? application?.description : '');
    const [applicationNameError, setApplicationNameError] = useState('');
    const [descriptionError, setDescriptionError] = useState('');
    const dpSystemApp = application?.attributes?.isDpSystemApp === "true" ? true: false;
    const applicationScopes = application?.attributes?.scopes ? 
        (application?.attributes?.scopes as string).split(";") : [];
    const [scopes, setScopes] = useState<string[]>(applicationScopes);
    const [isDpSystemApp, setIsDpSystemApp] = useState<boolean>(dpSystemApp);


    const editApplicationLabel = intl.formatMessage({
        id: 'application.create.editMode.title',
        defaultMessage: 'Edit Application'
    });

    const updatingApplication = intl.formatMessage({
        id: 'application.update.progress.label',
        defaultMessage: 'Updating Application: '
    });

    const createApplicationLabel = intl.formatMessage({
        id: 'application.create.createMode.title',
        defaultMessage: 'Create an Application'
    });

    const creatingApplication = intl.formatMessage({
        id: 'application.create.progress.label',
        defaultMessage: 'Creating Application: '
    });









    return (
        <React.Fragment>
            <Dialog
                open={open}
                onClose={(_,reason) => {
                    if (reason !== 'backdropClick') {
                        onClose()
                    }
                }}
                aria-labelledby="delete-app-dialog-title"
                fullWidth={!isInProgress}
                classes={{ paper: classes.customWidth }}
            >
                <DialogTitle>
                    <Typography className={classes.primaryTitle}>
                        {!isInProgress ?
                            (isEdit ?  editApplicationLabel : createApplicationLabel) :
                            (isEdit ? updatingApplication + application?.name : creatingApplication + applicationName)
                        }
                    </Typography>
                    {
                        !isInProgress && onClose ? (
                            <IconButton className={classes.closeButton} onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        ) : null
                    }
                </DialogTitle>
                <DialogContent classes={{ root: classes.createApiDialogContent }}>
                    <CreateApplicationFormContent 
                        isEdit={isEdit}
                        application={application}
                        redirectToApplication={redirectToApplication}
                        onClose={onClose}
                        />
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        applicationReducer: state.applicationReducer,
        userReducer: state.userReducer
    }
};

const mapDispatchToProps = {
    dispatchCreateApplication: createApplication,
    dispatchUpdateApplication: updateApplication,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateApp);
